import { createRouter, createWebHistory } from 'vue-router';
import store from '../store';
import { LOCAL_STORAGE_KEY } from '@/utils/constants';

function isAuthenticated() {
  return !!localStorage.getItem(LOCAL_STORAGE_KEY);
}

function hasValidSigAndEmail() {
  const sig = localStorage.getItem('karma_sig');
  const email = localStorage.getItem('karma_email');
  return !!sig && !!email;
}

function hasTemporaryToken() {
  const { temporaryToken } = store.state.auth;
  return !!temporaryToken;
}

function hasFirstAndLastName() {
  const { currentUser } = store.state.auth;
  return currentUser && currentUser.first_name && currentUser.last_name;
}

function ensureSubsOrTrial(to, from, next) {
  const requiresAccess = to.matched.some((record) => record.meta.requiresAccess);
  const billingActive = store.state.auth.currentWorkspace.billing_active;
  const billingAdministrator = store.state.auth.permissions?.billing_administrator;
  const isTrialRoute = to.name === 'Trial';
  const isSettingsRoute = to.fullPath.includes('settings');

  if (!requiresAccess || isTrialRoute) {
    return next();
  }

  if (billingActive) {
    return next();
  }

  if (billingAdministrator) {
    return isSettingsRoute ? next() : next('/settings/billing/trial');
  }

  return next('/expired');
}

function ensureDashboardVisit(to, from, next) {
  const { permissions } = store.state.auth;

  if (permissions.global_administrator || permissions.account_administrator) {
    next();
  } else {
    next('/');
  }
}

function ensureGlobalSettingsVisit(to, from, next) {
  const { permissions } = store.state.auth;

  if (permissions.global_administrator || permissions.account_administrator) {
    next();
  } else {
    next('/');
  }
}

function ensureNotificationsSettingsVisit(to, from, next) {
  const { permissions } = store.state.auth;

  if (permissions.global_administrator || permissions.account_administrator) {
    next();
  } else {
    next('/');
  }
}

function ensureValuesSettingsVisit(to, from, next) {
  const { permissions } = store.state.auth;

  if (permissions.global_administrator || permissions.account_administrator) {
    next();
  } else {
    next('/');
  }
}

function ensureTeamVisit(to, from, next) {
  const { permissions } = store.state.auth;

  if (permissions.global_administrator || permissions.account_administrator) {
    next();
  } else {
    next('/');
  }
}

function ensureRewardsVisit(to, from, next) {
  const { permissions } = store.state.auth;

  if (permissions.rewards_administrator || permissions.billing_administrator) {
    next();
  } else {
    next('/');
  }
}

function ensureDangersVisit(to, from, next) {
  const { permissions } = store.state.auth;

  if (permissions.account_administrator) {
    next();
  } else {
    next('/');
  }
}

function ensureBillingVisit(to, from, next) {
  const { permissions } = store.state.auth;

  if (permissions.billing_administrator) {
    next();
  } else {
    next('/');
  }
}

function ensureSettingsVisit(to, from, next) {
  const { permissions } = store.state.auth;

  if (
    permissions.global_administrator ||
    permissions.account_administrator ||
    permissions.billing_administrator ||
    permissions.rewards_administrator
  ) {
    next();
  } else {
    next('/');
  }
}

function ensureExpiredVisit(to, from, next) {
  const { permissions } = store.state.auth;
  const billingActive = store.state.auth.currentWorkspace.billing_active;

  if (billingActive) {
    next('/');
  } else if (permissions.billing_administrator) {
    next('/settings/billing/trial');
  } else {
    next();
  }
}

async function fetchUserData(to, from, next) {
  try {
    store.commit('profile/setLoading', true);
    await store.dispatch('profile/loadUserProfile', to.params.id);
    await store.dispatch('profile/loadTopPoints', to.params.id);
    await store.dispatch('profile/loadUserValues', to.params.id);
    await store.dispatch('profile/loadUserActivity', to.params.id);
    next();
  } catch (error) {
    next('/404');
  } finally {
    store.commit('profile/setLoading', false);
  }
}

async function fetchSettings(to, from, next) {
  try {
    await store.dispatch('settings/loadSettings');
    next();
  } catch (error) {
    if (error.response && error.response.status === 404) {
      next('/404');
    } else {
      console.error('Error:', error);
    }
  }
}

async function fetchValues(to, from, next) {
  try {
    await store.dispatch('auth/loadValues');
    next();
  } catch (error) {
    if (error.response && error.response.status === 404) {
      next('/404');
    } else {
      console.error('Error:', error);
    }
  }
}

async function fetchRewards(to, from, next) {
  const { permissions } = store.state.auth;

  if (permissions.rewards_administrator) {
    try {
      await store.dispatch('settings/loadRewards');
      next();
    } catch (error) {
      if (error.response && error.response.status === 404) {
        next('/404');
      } else {
        console.error('Error:', error);
      }
    }
  } else if (permissions.billing_administrator) {
    next();
  } else {
    next('/404');
  }
}
async function fetchPurchasesRequests(to, from, next) {
  const { permissions } = store.state.auth;

  if (permissions.rewards_administrator) {
    try {
      await store.dispatch('settings/initPurchases');
      next();
    } catch (error) {
      if (error.response && error.response.status === 404) {
        next('/404');
      } else {
        console.error('Error:', error);
      }
    }
  } else if (permissions.billing_administrator) {
    next();
  } else {
    next('/404');
  }
}

async function fetchRewardsInShop(to, from, next) {
  try {
    await store.dispatch('shop/loadRewards');
    next();
  } catch (error) {
    if (error.response && error.response.status === 404) {
      next('/404');
    } else {
      console.error('Error:', error);
    }
  }
}

async function fetchPurchasesInShop(to, from, next) {
  try {
    await store.dispatch('shop/loadPurchases');
    next();
  } catch (error) {
    if (error.response && error.response.status === 404) {
      next('/404');
    } else {
      console.error('Error:', error);
    }
  }
}

async function fetchCurrentUser(to, from, next) {
  try {
    await store.dispatch('auth/getCurrentUser');
    next();
  } catch (error) {
    if (error.response && error.response.status === 404) {
      next('/404');
    } else {
      console.error('Error:', error);
    }
  }
}

function beforeEnterRouteSpecificChecks(to, from, next) {
  switch (to.name) {
    case 'CodeCheck':
      if (!hasValidSigAndEmail()) {
        next('/login');
      } else {
        next();
      }
      break;
    case 'CreateTeam':
    case 'Workspaces':
      if (!hasTemporaryToken()) {
        next('/login');
      } else {
        next();
      }
      break;
    default:
      next();
  }
}

async function ensureSubscribed(to, from, next) {
  await store.dispatch('billing/init');
  const onlyTrialPage = store.getters['billing/onlyTrialPage'];

  if (onlyTrialPage && !['Billing', 'Trial'].includes(to.name)) {
    next('/settings/billing/trial');
  } else if (!onlyTrialPage && to.name === 'Trial') {
    next('/settings/billing/overview');
  } else {
    next();
  }
}

const routes = [
  {
    path: '/feed',
    name: 'Feed',
    component: () => import('../views/feed/FeedPage.vue'),
    meta: {
      requiresAuth: true,
      requiresFirstAndLastName: true,
      requiresAccess: true,
    },
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/auth/LoginPage.vue'),
    meta: {
      requiresAuth: false,
      requiresFirstAndLastName: false,
      requiresAccess: false,
    },
    props: { formType: 'login' },
  },
  {
    path: '/login/accept',
    name: 'Invitation',
    component: () => import('../views/auth/InvitationPage.vue'),
    meta: {
      requiresAuth: false,
      requiresFirstAndLastName: false,
      requiresAccess: false,
    },
  },
  {
    path: '/sign-up',
    component: () => import('../views/auth/LoginPage.vue'),
    meta: {
      requiresAuth: false,
      requiresFirstAndLastName: false,
      requiresAccess: false,
    },
    props: { formType: 'sign_up' },
  },
  {
    path: '/code-check',
    name: 'CodeCheck',
    component: () => import('../views/auth/CodeCheckPage.vue'),
    meta: {
      requiresAuth: false,
      requiresFirstAndLastName: false,
      requiresAccess: false,
    },
  },
  {
    path: '/create-team',
    name: 'CreateTeam',
    component: () => import('../views/auth/CreateTeamPage.vue'),
    meta: {
      requiresAuth: false,
      requiresAccess: false,
    },
  },
  {
    path: '/create-profile',
    name: 'CreateProfile',
    component: () => import('../views/auth/CreateProfilePage.vue'),
    meta: {
      requiresAuth: true,
      requiresFirstAndLastName: false,
      requiresAccess: true,
    },
  },
  {
    path: '/add-photo',
    name: 'AddPhoto',
    component: () => import('../views/auth/AddPhotoPage.vue'),
    meta: {
      requiresAuth: true,
      requiresFirstAndLastName: true,
      requiresAccess: true,
    },
  },
  {
    path: '/welcome',
    name: 'Welcome',
    component: () => import('../views/auth/WelcomePage.vue'),
    meta: {
      requiresAuth: true,
      requiresFirstAndLastName: true,
      requiresAccess: true,
    },
  },
  {
    path: '/404',
    name: 'NotFound',
    component: () => import('../views/error/404Page.vue'),
    meta: {
      requiresAuth: true,
      requiresFirstAndLastName: false,
      requiresAccess: true,
    },
  },
  {
    path: '/goodbye',
    name: 'GoodBye',
    component: () => import('../views/goodbye/GoodByePage.vue'),
    meta: {
      requiresAuth: false,
      requiresFirstAndLastName: false,
      requiresAccess: false,
    },
  },
  {
    path: '/workspaces',
    name: 'Workspaces',
    component: () => import('../views/auth/WorkspacesPage.vue'),
    meta: {
      requiresAuth: false,
      requiresFirstAndLastName: false,
      requiresAccess: false,
    },
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('../views/dashboard/DashboardPage.vue'),
    beforeEnter: [ensureDashboardVisit],
    meta: {
      requiresAuth: true,
      requiresFirstAndLastName: true,
      requiresAccess: true,
    },
  },
  {
    path: '/settings',
    component: () => import('../views/settings/SettingsIndex.vue'),
    children: [
      {
        path: '',
        name: 'Menu',
        component: () => import(/* webpackChunkName: "settings" */ '../views/settings/SettingsMenu.vue'),
      },
      {
        path: 'global',
        name: 'Global',
        component: () => import(/* webpackChunkName: "settings" */ '../views/settings/SettingsGlobal.vue'),
        beforeEnter: [ensureGlobalSettingsVisit],
      },
      {
        path: 'team',
        beforeEnter: [ensureTeamVisit],
        children: [
          {
            path: '',
            name: 'Team',
            component: () => import(/* webpackChunkName: "settings" */ '../views/settings/SettingsTeam.vue'),
          },
          {
            path: 'import',
            name: 'Import',
            component: () => import('../views/settings/components/team/ImportCsv.vue'),
          },
          {
            path: 'add-user',
            name: 'AddUser',
            component: () => import('../views/settings/components/team/AddUser.vue'),
          },
          {
            path: 'bulk-upload',
            name: 'bulkUpload',
            component: () => import('../views/settings/components/team/BulkUpload.vue'),
          },
        ],
      },
      {
        path: 'notifications',
        name: 'Notifications',
        component: () => import(/* webpackChunkName: "settings" */ '../views/settings/SettingsNotifications.vue'),
        beforeEnter: [ensureNotificationsSettingsVisit],
      },
      {
        path: 'values',
        name: 'Values',
        component: () => import('../views/settings/SettingsValues.vue'),
        beforeEnter: [ensureValuesSettingsVisit, fetchValues],
      },

      {
        path: 'danger',
        name: 'Danger',
        component: () => import('../views/settings/DangerZone.vue'),
        beforeEnter: [ensureDangersVisit],
      },
      {
        path: 'billing',
        name: 'Billing',
        component: () => import('../views/settings/SettingsBilling.vue'),
        beforeEnter: [ensureSubscribed, ensureBillingVisit],
        children: [
          {
            path: 'overview',
            name: 'Overview',
            component: () => import('../views/settings/components/billing/BillingOverview.vue'),
          },
          {
            path: 'history',
            name: 'History',
            component: () => import('../views/settings/components/billing/BillingHistory.vue'),
          },
          {
            path: 'details',
            name: 'Details',
            component: () => import('../views/settings/components/billing/BillingDetails.vue'),
          },
          {
            path: 'payment_method',
            name: 'Payment',
            component: () => import('../views/settings/components/billing/BillingPayment.vue'),
          },
          {
            path: 'trial',
            name: 'Trial',
            component: () => import('../views/settings/components/billing/BillingTrial.vue'),
          },
          {
            path: '',
            redirect: () => `/settings/billing/${store.state.auth.currentWorkspace?.billing_active ? 'overview' : 'trial'}`,
          },
        ],
      },
      {
        path: 'rewards',
        name: 'Rewards',
        component: () => import('../views/settings/SettingsRewards.vue'),
        beforeEnter: [ensureRewardsVisit, fetchRewards, fetchPurchasesRequests],
        children: [
          {
            path: 'new',
            name: 'NewReward',
            component: () => import('../views/settings/OneRewardPage.vue'),
          },
          {
            path: 'edit/:id',
            name: 'EditReward',
            component: () => import('../views/settings/EditRewardPage.vue'),
            props: true,
          },
          {
            path: 'all',
            name: 'AllRewards',
            component: () => import('../views/settings/AllRewardsPage.vue'),
          },
          { path: '', redirect: '/settings/rewards/all' },
        ],
      },
      { path: '*', redirect: 'Menu' },
    ],
    meta: {
      requiresAuth: true,
      requiresFirstAndLastName: true,
      requiresAccess: true,
    },
    beforeEnter: [ensureSettingsVisit, fetchSettings],
  },
  {
    path: '/profile/:id',
    name: 'Profile',
    component: () => import('../views/profile/ProfilePage.vue'),
    meta: {
      requiresAuth: true,
      requiresFirstAndLastName: true,
      requiresAccess: true,
    },
    beforeEnter: [fetchUserData],
  },
  {
    path: '/edit-profile',
    name: 'EditProfile',
    component: () => import('../views/profile/EditProfilePage.vue'),
    meta: {
      requiresAuth: true,
      requiresFirstAndLastName: true,
      requiresAccess: true,
    },
  },
  {
    path: '/rewards',
    name: 'RewardsShop',
    component: () => import('../views/rewards/RewardsShop.vue'),
    meta: {
      requiresAuth: true,
      requiresFirstAndLastName: true,
      requiresAccess: true,
    },
    beforeEnter: [fetchRewardsInShop, fetchPurchasesInShop, fetchCurrentUser],
  },
  {
    path: '/expired',
    name: 'AccessLimited',
    component: () => import('../views/expired/ExpiredPage.vue'),
    beforeEnter: [ensureExpiredVisit],
    meta: {
      requiresAuth: true,
      requiresFirstAndLastName: false,
      requiresAccess: false,
    },
  },
  {
    path: '/loading',
    name: 'LoadingPage',
    component: () => import('../views/loading/LoadingPage.vue'),
    meta: {
      requiresAuth: true,
      requiresFirstAndLastName: true,
      requiresAccess: false,
    },
    beforeEnter: async (to, from, next) => {
      if (to.query.id) {
        next();
      } else {
        next('/feed');
      }
    },
  },
  {
    path: '/impersonate/:token',
    name: 'impersonate',
    beforeEnter: async (to, from, next) => {
      try {
        await store.dispatch('auth/impersonate', to.params);
        next('/feed');
      } catch (e) {
        next('/login');
      }
    },
  },
  // Other routes...
  { path: '/', redirect: 'feed' },
  { path: '/:pathMatch(.*)*', name: 'NotFound', component: () => import('../views/error/404Page.vue') },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      };
    } else if (to.name === 'Profile' || to.name === 'EditReward') {
      window.scrollTo(0, 0);
    } else if (savedPosition) {
      return savedPosition;
    }
  },
});

router.beforeEach((to, from, next) => {
  const isAuth = isAuthenticated();
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const requiresFirstAndLastName = to.matched.some((record) => record.meta.requiresFirstAndLastName);
  const hasNames = hasFirstAndLastName();

  if (!isAuth && to.path.match(/^\/goodbye\/?$/)) {
    return next();
  }

  if (requiresAuth && !isAuth) {
    store.dispatch('auth/logout');
    return next('/login');
  }

  if (isAuth && to.path.match(/^\/login\/?$/)) {
    return next('/feed');
  }

  if (requiresFirstAndLastName && !hasNames) {
    return next('/create-profile');
  }

  next();
});
router.beforeEach(beforeEnterRouteSpecificChecks);
router.beforeEach(ensureSubsOrTrial);

export default router;
